import api from 'core/util/api';
import { DeviceSubtypes } from '@kentik/ui-shared/dictionary/types';
import { UseQueryOptions } from '@tanstack/react-query';
import { useDeviceUniqueSubtypes } from 'app/stores/dictionary/mobx/useDeviceUniqueSubtypes';
import { useCallback } from 'react';

const fetchFn = () => api.get('/api/ui/dictionary/deviceSubtypes') as Promise<DeviceSubtypes>;

let cacheKeyData: DeviceSubtypes | null = null;
let cacheKeySet: Set<string> | null = null;
let cachedResult: DeviceSubtypes | null = null;

/**
 * Very simple function which filters device subtypes by unique device subtypes and then caches the result.
 * Since we don't need to worry about multiple cache entries, this implementation is sufficient.
 */
const filterSubtypesAndCache = (data: DeviceSubtypes, uniqueSubtypes: Set<string>): DeviceSubtypes => {
  if (!data?.cachedTypes) {
    return { cachedColumns: {}, cachedTypes: {} };
  }

  if (cacheKeyData === data && cacheKeySet === uniqueSubtypes && cachedResult) {
    return cachedResult;
  }

  cachedResult = {
    cachedTypes: Object.fromEntries(
      Object.entries(data.cachedTypes).filter(([_, { device_subtype }]) => uniqueSubtypes.has(device_subtype))
    ),
    cachedColumns: Object.fromEntries(Object.entries(data.cachedColumns).filter(([key]) => uniqueSubtypes.has(key)))
  };

  cacheKeyData = data;
  cacheKeySet = uniqueSubtypes;

  return cachedResult;
};

/**
 * Query for device subtypes. Ensure that you reuse the `select` function if you are extending this query, as this provides useful
 * filtering to trim down your results!
 */
export const useDeviceSubtypesQuery = () => {
  const uniqueSubtypes = useDeviceUniqueSubtypes();
  const select = useCallback(
    (data: DeviceSubtypes): DeviceSubtypes => filterSubtypesAndCache(data, uniqueSubtypes),
    [uniqueSubtypes]
  );

  return {
    queryKey: ['deviceSubtypesRaw'] as const,
    queryFn: fetchFn,
    staleTime: 300000,
    select
  } as const satisfies UseQueryOptions<DeviceSubtypes, Error, DeviceSubtypes, readonly ['deviceSubtypesRaw']>;
};
